import { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import FetchWithGif from '../../../Reutilizables/FetchWithGif';
import { getBase64FromImage } from '../../../../utilities/pdfLogo';
import logo from '../../../../img/logo/Logo_FICSULLANA.png';
import { toast } from 'react-toastify';

const ReporteFiltroCuotas = ({
  cuotas,
  selectedAsesor,
  asesores,
  startDate,
  endDate,
  estado,
  dni,
  selectedGroup,
  onComplete,
  onPDFGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '-';
    }
  };

  const toNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getOtros = (cuota) => (
    cuota.otros !== null
      ? toNumber(cuota.otros)
      : toNumber(cuota.monto) - toNumber(cuota.capital) - toNumber(cuota.interes)
  );

  useEffect(() => {
    generateElegantPDF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateElegantPDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const addPrefixAndLeadingZeros = (id, prefix, totalLength) => {
        const idStr = String(id);
        const zerosNeeded = totalLength - (prefix.length + idStr.length);
        const paddedId = '0'.repeat(zerosNeeded) + idStr;
        return prefix + paddedId;
      };

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let y = 15;
      const today = new Date();
      const fechaHoy = format(today, 'dd/MM/yyyy');

      const addLogo = async () => {
        try {
          const logoBase64 = await getBase64FromImage(logo);
          const logoWidth = 48;
          const logoHeight = 51;
          const logoX = margin;
          const headerY = 9;
          doc.addImage(logoBase64, 'PNG', logoX, headerY, logoWidth, logoHeight);
        } catch {
        }
      };

      const checkPageSpace = (requiredSpace) => {
        if (y + requiredSpace > pageHeight - 50) {
          doc.addPage();
          y = 20;
          return true;
        }
        return false;
      };

      const drawSection = (title, height = 6) => {
        checkPageSpace(height + 5);
        doc.setFillColor(230, 0, 0);
        doc.rect(margin, y, pageWidth - 2 * margin, height, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(title, margin + 5, y + 4.5);
        y += height + 1;
        return y;
      };

      const drawInfoBox = (items, boxHeight = 35) => {
        checkPageSpace(boxHeight + 5);
        y += 6;
        items.forEach((item, index) => {
          if (index > 0 && index % 2 === 0) y += 5;
          const xPos = index % 2 === 0 ? margin + 6 : margin + (pageWidth - 2 * margin) / 2 + 6;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.setTextColor(108, 122, 137);
          doc.text(item.label + ':', xPos, y);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(44, 62, 80);
          const maxWidth = (pageWidth - 2 * margin) / 2 - 15;
          const wrappedText = doc.splitTextToSize(item.value || '-', maxWidth);
          doc.text(wrappedText[0], xPos, y + 4);
          if (index % 2 === 1) y += 6;
        });
        if (items.length % 2 === 1) y += 6;
        y += 6;
      };

      await addLogo();

      // Header
      const textX = pageWidth - margin;
      const headerY = 27;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(44, 62, 80);
      doc.text('REPORTE DE CUOTAS', textX, headerY + 6, { align: 'right' });
      doc.setFontSize(10);
      doc.text('[REPORTE DE FILTRADO]', textX, headerY + 12, { align: 'right' });
      y += 33;

      // Información de filtros
      const selectedAsesorData = asesores.find(asesor => asesor.id === parseInt(selectedAsesor));

      drawSection('1. INFORMACIÓN DE FILTROS APLICADOS');

      const filterInfo = [
        { label: 'Periodo', value: `${startDate ? formatDate(startDate) : 'No especificado'} - ${endDate ? formatDate(endDate) : 'No especificado'}` },
        { label: 'Estado', value: estado || 'TODOS LOS ESTADOS' },
        { label: 'Asesor', value: selectedAsesorData ? selectedAsesorData.nombre : 'TODOS LOS ASESORES' },
        { label: 'Grupo', value: selectedGroup || 'TODOS LOS GRUPOS' },
        { label: 'Total de registros', value: cuotas.length.toString() },
        { label: 'DNI', value: dni || 'TODOS' }
      ];

      drawInfoBox(filterInfo, 30);

      // Sección de detalles con autoTable
      drawSection('2. DETALLES DE CUOTAS');

      const headers = [
        'ID Prestamo',
        'Cliente',
        'N° Cuota',
        'F. Vencimiento',
        'F. Pago',
        'Capital',
        'Interés',
        'Días Mora',
        'Mora',
        'Otros',
        'Total Cuota',
        'Saldo Pend.',
        'Estado',
        'Asesor'
      ];

      const data = cuotas.map(cuota => [
        addPrefixAndLeadingZeros(cuota.idPrestamo, '79', 10),
        cuota.cliente_nombre || '-',
        cuota.numero_cuota?.toString() || '-',
        formatDate(cuota.fecha_vencimiento),
        formatDate(cuota.fechaPago),
        `S/ ${toNumber(cuota.capital).toFixed(2)}`,
        `S/ ${toNumber(cuota.interes).toFixed(2)}`,
        cuota.dias_mora?.toString() || '0',
        cuota.mora_reducida > 0
          ? `S/ ${toNumber(cuota.mora).toFixed(2)} (-${cuota.mora_reducida}%)`
          : `S/ ${toNumber(cuota.mora).toFixed(2)}`,
        `S/ ${getOtros(cuota).toFixed(2)}`,
        `S/ ${toNumber(cuota.monto).toFixed(2)}`,
        `S/ ${toNumber(cuota.saldo_pendiente).toFixed(2)}`,
        cuota.estado || '-',
        cuota.asesor_nombre || '-'
      ]);

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: y,
        theme: 'striped',
        margin: { left: margin, right: margin },
        tableWidth: pageWidth - 2 * margin,
        headStyles: {
          fillColor: [230, 0, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          lineWidth: 0,
        },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          valign: 'middle',
          overflow: 'linebreak',
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
          halign: 'center',
        },
        bodyStyles: {
          textColor: [44, 62, 80],
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { halign: 'left' },   // Cliente
          1: { halign: 'center' }, // N° Cuota
          2: { halign: 'center' }, // F. Vencimiento
          3: { halign: 'center' }, // Capital
          4: { halign: 'center' }, // Interés
          5: { halign: 'center' }, // Días Mora
          6: { halign: 'center' }, // Mora
          7: { halign: 'center' }, // Otros
          8: { halign: 'center' }, // Total Cuota
          9: { halign: 'center' }, // Estado
          10: { halign: 'center' } // Asesor
        },
        didDrawCell: function (data) {
          if (data.row.index === 0 && data.section === 'head') {
            const { doc, cell } = data;
            doc.setDrawColor(230, 0, 0);
            doc.setLineWidth(1);
            doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
          }
        },
      });

      y = doc.lastAutoTable.finalY + 10;

      const totalsHeaders = ['CONCEPTO', 'MONTO'];
      const totalsData = [
        ['CAPITAL', `S/ ${cuotas.reduce((sum, cuota) => sum + toNumber(cuota.capital), 0).toFixed(2)}`],
        ['INTERÉS', `S/ ${cuotas.reduce((sum, cuota) => sum + toNumber(cuota.interes), 0).toFixed(2)}`],
        ['OTROS', `S/ ${cuotas.reduce((sum, cuota) => sum + getOtros(cuota), 0).toFixed(2)}`],
        ['TOTAL CUOTA', `S/ ${cuotas.reduce((sum, cuota) => sum + toNumber(cuota.monto), 0).toFixed(2)}`],
        ['SALDO PENDIENTE', `S/ ${cuotas.reduce((sum, cuota) => sum + toNumber(cuota.saldo_pendiente), 0).toFixed(2)}`]
      ];

      autoTable(doc, {
        head: [totalsHeaders],
        body: totalsData,
        startY: y,
        theme: 'striped',
        margin: { left: 15 },
        tableWidth: 70,
        headStyles: {
          fillColor: [230, 0, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          valign: 'middle',
        },
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          valign: 'middle',
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
        },
        bodyStyles: {
          textColor: [44, 62, 80],
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'bold' },
          1: { halign: 'right', fontStyle: 'bold' }
        },
        didParseCell: function (data) {
          if (data.row.index === data.table.body.length - 1) {
            data.cell.styles.fillColor = [230, 0, 0];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 9;
          }
        },
      });

      // Pie de página
      const finalY = pageHeight - 20;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Fecha de emisión: ${fechaHoy}`, pageWidth / 2, finalY + 3, { align: 'center' });

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      if (onPDFGenerated) {
        onPDFGenerated(pdfUrl);
      }

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      if (onComplete) {
        onComplete();
      }
      toast.error('Error al generar el PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return isGenerating ? <FetchWithGif /> : null;
};

export default ReporteFiltroCuotas;
